/**
 * useFeatures — fetch the caller's effective feature flags (from their
 * operator) and expose them. All features default to ON if the call fails
 * (so a broken /api/me/features endpoint doesn't lock people out of the app).
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DEFAULTS = {
    sportsbook: true,
    casino: true,
    lottery: true,
    daily_specials: true,
    lucky_pack: true,
    series_pack: true,
    tournaments: true,
    poker: true,
    free_play: true,
    jackpots: true,
    cashier_cashout: true,
    player_self_cashout: true,
};

let _cache = null;
const _subscribers = new Set();

const broadcast = (val) => {
    _cache = val;
    _subscribers.forEach(fn => { try { fn(val); } catch { /* noop */ } });
};

export const refreshFeatures = async () => {
    try {
        const { data } = await axios.get(`${API}/me/features`, { withCredentials: true });
        const features = { ...DEFAULTS, ...(data?.features || {}) };
        broadcast({ features, operator_id: data?.operator_id || null, role: data?.role });
    } catch {
        broadcast({ features: DEFAULTS, operator_id: null, role: null });
    }
};

export const useFeatures = () => {
    const { user } = useAuth();
    const [state, setState] = useState(_cache || { features: DEFAULTS, operator_id: null, role: null });

    useEffect(() => {
        _subscribers.add(setState);
        if (!user) {
            // Logged out — restore defaults
            if (_cache) broadcast({ features: DEFAULTS, operator_id: null, role: null });
        } else if (!_cache || _cache.role !== user.role) {
            refreshFeatures();
        }
        return () => { _subscribers.delete(setState); };
    }, [user]);

    return state.features || DEFAULTS;
};
